import { useMemo, useReducer } from "react";
import type { Dispatch, SetStateAction } from "react";
import type {
  AccountabilityData,
  EthicsData,
  LeadershipData,
  TransitionMeasure,
  TransparencyData,
} from "./types";

type GovernanceState = {
  ethicsData: EthicsData;
  transparencyData: TransparencyData;
  leadershipData: LeadershipData;
  accountabilityData: AccountabilityData;
  transitionMeasuresByTopic: Record<string, TransitionMeasure[]>;
};

type GovernanceAction =
  | { type: "setEthicsData"; payload: SetStateAction<EthicsData> }
  | { type: "updateEthicsData"; payload: Partial<EthicsData> }
  | { type: "setTransparencyData"; payload: SetStateAction<TransparencyData> }
  | { type: "updateTransparencyData"; payload: Partial<TransparencyData> }
  | { type: "setLeadershipData"; payload: SetStateAction<LeadershipData> }
  | { type: "updateLeadershipData"; payload: Partial<LeadershipData> }
  | { type: "setAccountabilityData"; payload: SetStateAction<AccountabilityData> }
  | { type: "updateAccountabilityData"; payload: Partial<AccountabilityData> }
  | { type: "setTransitionMeasuresForTopic"; payload: { topicId: string; measures: TransitionMeasure[] } };

const initialGovernanceState: GovernanceState = {
  ethicsData: {
    hasConvictions: null,
    convictionsCount: "",
    finesAmount: "",
    finesCurrency: "usd",
    antiCorruptionPolicy: null,
    whistleblowerMechanism: null,
    evidenceFiles: [],
  },
  transparencyData: {
    hasFinancialStatements: null,
    financialStatementsFrequency: "annual",
    hasExternalAudit: null,
    stakeholderCommunication: "",
    supportingDocumentsLinks: "",
    supportingDocuments: [],
  },
  leadershipData: {
    hasGovernanceBody: null,
    femaleBoardMembers: "",
    maleBoardMembers: "",
    boardMembersTotal: "",
    boardDiversityFemalePercent: "",
  },
  accountabilityData: {
    sustainabilityOwnerRole: "",
    controversialSectorsRevenue: {},
    benchmarkExclusion: null,
  },
  transitionMeasuresByTopic: {},
};

function resolveSetState<T>(value: SetStateAction<T>, current: T): T {
  return typeof value === "function" ? value(current) : value;
}

function governanceReducer(state: GovernanceState, action: GovernanceAction): GovernanceState {
  switch (action.type) {
    case "setEthicsData":
      return { ...state, ethicsData: resolveSetState(action.payload, state.ethicsData) };
    case "updateEthicsData":
      return { ...state, ethicsData: { ...state.ethicsData, ...action.payload } };
    case "setTransparencyData":
      return { ...state, transparencyData: resolveSetState(action.payload, state.transparencyData) };
    case "updateTransparencyData":
      return { ...state, transparencyData: { ...state.transparencyData, ...action.payload } };
    case "setLeadershipData":
      return { ...state, leadershipData: resolveSetState(action.payload, state.leadershipData) };
    case "updateLeadershipData":
      return { ...state, leadershipData: { ...state.leadershipData, ...action.payload } };
    case "setAccountabilityData":
      return { ...state, accountabilityData: resolveSetState(action.payload, state.accountabilityData) };
    case "updateAccountabilityData":
      return { ...state, accountabilityData: { ...state.accountabilityData, ...action.payload } };
    case "setTransitionMeasuresForTopic":
      return {
        ...state,
        transitionMeasuresByTopic: { ...state.transitionMeasuresByTopic, [action.payload.topicId]: action.payload.measures },
      };
    default:
      return state;
  }
}

type GovernanceActions = {
  setEthicsData: Dispatch<SetStateAction<EthicsData>>;
  updateEthicsData: (updates: Partial<EthicsData>) => void;
  setTransparencyData: Dispatch<SetStateAction<TransparencyData>>;
  updateTransparencyData: (updates: Partial<TransparencyData>) => void;
  setLeadershipData: Dispatch<SetStateAction<LeadershipData>>;
  updateLeadershipData: (updates: Partial<LeadershipData>) => void;
  setAccountabilityData: Dispatch<SetStateAction<AccountabilityData>>;
  updateAccountabilityData: (updates: Partial<AccountabilityData>) => void;
  setTransitionMeasuresForTopic: (topicId: string, measures: TransitionMeasure[]) => void;
};

export function useGovernanceState(): { state: GovernanceState; actions: GovernanceActions } {
  const [state, dispatch] = useReducer(governanceReducer, initialGovernanceState);

  const actions = useMemo<GovernanceActions>(
    () => ({
      setEthicsData: (value) => dispatch({ type: "setEthicsData", payload: value }),
      updateEthicsData: (updates) => dispatch({ type: "updateEthicsData", payload: updates }),
      setTransparencyData: (value) => dispatch({ type: "setTransparencyData", payload: value }),
      updateTransparencyData: (updates) => dispatch({ type: "updateTransparencyData", payload: updates }),
      setLeadershipData: (value) => dispatch({ type: "setLeadershipData", payload: value }),
      updateLeadershipData: (updates) => dispatch({ type: "updateLeadershipData", payload: updates }),
      setAccountabilityData: (value) => dispatch({ type: "setAccountabilityData", payload: value }),
      updateAccountabilityData: (updates) => dispatch({ type: "updateAccountabilityData", payload: updates }),
      setTransitionMeasuresForTopic: (topicId, measures) =>
        dispatch({ type: "setTransitionMeasuresForTopic", payload: { topicId, measures } }),
    }),
    []
  );

  return { state, actions };
}
